import { Button, useModal } from '@billy/design-system'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { useRetryBillIntegrationSyncMutation } from '@features/attachments/hooks/useRetryBillSync'
import { Bill } from '@features/bills/types/bill'
import { useDeleteBillAction } from '@views/bills/elements/BillEditableForm/hooks/useDeleteBillAction'
import { DeleteBillConfirmationModal } from '@views/bills/elements/DeleteBillConfirmationModal'
import { getLatestIntegrationStatus } from '@views/bills/utils/getLatestIntegrationStatus'
import { isBillDeletable } from '@views/bills/utils/isBillDeletable'
import { isBillSyncRetryable } from '@views/bills/utils/isBillSyncRetryable'

import { ModalId } from '../../../../../../../src/enums/modalId'
import { useOrganizationViewUrl } from '../../../../../../../src/hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../../src/utils/routing/reactRoute'

export const BillIntegrationMessageActions = ({ bill }: { bill?: Bill }) => {
  const { t } = useTranslation()

  const location = useLocation()
  const navigate = useNavigate()

  const { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } = useModal(
    ModalId.BillDeleteConfirmationModal,
  )
  const { retryIntegration, isLoading } = useRetryBillIntegrationSyncMutation({
    integrationStatus: getLatestIntegrationStatus(bill?.integrationStatus),
  })

  const { url: billsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getMainRoute())

  const { deleteBill, isDeleting } = useDeleteBillAction({
    onSuccess: async () => {
      closeDeleteConfirmationModal()
      navigate(`${billsUrl}${location.search}`, { replace: true })
    },
  })

  const handleDeleteBill = () => {
    if (!bill) {
      return
    }
    deleteBill(bill.id)
  }

  if (!bill) {
    return null
  }

  const isDeletable = isBillDeletable({
    bill,
    integrationStatus: bill.integrationStatus,
  })

  const isRetryable = isBillSyncRetryable({
    integrationStatus: bill.integrationStatus,
  })

  if (!isDeletable && !isRetryable) {
    return null
  }

  return (
    <div>
      {isDeletable ? (
        <Button onClick={() => openDeleteConfirmationModal()} color="danger">
          {t('bill.action.delete')}
        </Button>
      ) : null}

      {isRetryable ? (
        <Button disabled={isLoading} onClick={() => retryIntegration()} variant="text" color="primary">
          {t('bill.action.retry_sync')}
        </Button>
      ) : null}

      <DeleteBillConfirmationModal
        id={ModalId.BillDeleteConfirmationModal}
        onOk={handleDeleteBill}
        isDeleting={isDeleting}
      />
    </div>
  )
}
